




























































import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from '@vue/composition-api';
import { debounce } from 'lodash';

import store, { updateModuleState } from '@/store';
import { CommodityPo } from '@/po/commodity';
import { getAgreementsByCommodities, getDcContractList, getAbTestContract } from '@/api/contract';
import { ContractEnum } from '@/enums/contract-type';
import ForceReadBtn, { ButtonStatusEnum, ButtonTypeEnum } from '@/components/force-read-btn';
import type {
  Contract, AbTestContractResult, Commodity,
} from '@/api/contract/types';
import {
  ContractSceneTypeEnum,
} from '@/api/contract/types';
import { ABResEnum } from '@/enums/abExperiment';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { getGalileoAb } from '@/shared/galileoAb';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import { ubtSendError } from '@/shared/utils/ubt';
import {
  nativeLocalGetData,
  nativeLocalSetData,
} from '@/api/native/utils';
import { UserStayStatusSceneEnum } from '@/api/preApplyChoose/types';
import {
  SuperMemberSourceEnum,
} from '@/api/rights/types';
import { getWisdomConfig } from '@/shared/wisdom';
import type { ProtocolExposeDataType } from '../types';
import { PreApplyPageTypeEnum, ApplyModeEnum } from '../types';
import { trackConfig } from '../Configs';
import { checkIsOpenGPS, chooseNoChannelRouter } from '../utils';

import SuperMemberCard from '../components/super-member-card';
import ContractControlGroup from '../components/contract-control-group/index.vue';
import ContractVersionOne from '../components/contract-version-one/index.vue';
import ContractVersionTwo from '../components/contract-version-two/index.vue';

export default defineComponent({
  components: {
    ContractControlGroup,
    ContractVersionOne,
    ContractVersionTwo,
    ForceReadBtn,
  },
  props: {
    userViewAgreementStartTime: {
      type: Number,
      default: 0,
    },
    isOnlyDcHalf: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'loadAgreements',
    'submit',
    'reportUserStayStatus',
    'setUserViewAgreementStartTime',
  ],
  setup(props, context) {
    const agreementContentRef = ref();
    const contractBottomRef = ref();

    /** 用户查看协议的定时器 */
    const userViewAgreementTimer = ref<NodeJS.Timeout | null>(null);

    /** 需要补充基本信息 */
    const needSupplement = computed(
      () => store.state.home.creditData?.fillInTheInformation !== UserInfoStatus.Completed,
    );

    /** 是否有自营渠道8180(不包含跟贷) */
    const hasAutarkyCommodity = computed(
      () => store.state.preApplyChoose?.applyChooseList?.commoditys?.some(
        (it) => it.commodityId === 8180 && !it.followLoan),
    );
    /**
     * 用户在服务端归因的附加参数(非自营渠道暂不关联)
     * TODO:先临时处理，待后续服务端优化闭环
     */
    const serviceUbtExtra = computed(
      () => (hasAutarkyCommodity.value ? store.state.preApplyChoose.applyChooseList?.extra : {}),
    );
    /** 是否半流程授信 */
    const isDcHalfApply = computed(() => store.state.preApplyChoose.isDcHalfApply);
    /** 流量分发数据 */
    const applyChooseList = computed(() => store.state.preApplyChoose.applyChooseList);
    /** 流量分发渠道相关数据 */
    const commodities = computed(() => applyChooseList.value?.commoditys || []);
    /** 是否并行授信 */
    const isParallel = computed(
      () => (applyChooseList.value?.applyMode) === ApplyModeEnum.parallel,
    );

    /** 渠道信息+协议重组后集合 */
    const channelList = ref<CommodityPo.Info[]>([]);
    /** 页面是否滚动到过底 */
    const arriveBottom = ref(true);
    /** 授信页面合规规则（强读、下拉底部） */
    const contractRule = ref<AbTestContractResult>({});
    /** 页面底部padding-bottom类名，与是否展示会员卡有关 */
    const paddingBottomClass = ref('');
    /** 是否需要购买会员卡 */
    const needBuySuperMember = ref(false);
    /** 是否展示会员卡（已够/未购） */
    const showMemberCard = ref(false);
    /** 页面版本 */
    const pageVersion = ref('');

    /** 会员展示时按钮文案 */
    const superMemberButtonText = computed(() => (needBuySuperMember.value ? '已阅读并同意相关协议' : ''));

    /** 渠道列表 */
    const commodityIdList = computed(
      () => commodities.value.map((item) => item.commodityId),
    );

    /** 当前选中的渠道列表 */
    const selectedCommodity = computed(
      () => channelList.value.filter((item) => item.isSelected) || [],
    );

    /** 当前选中渠道的id集合 */
    const commodityIds = computed(
      () => selectedCommodity.value.map((item) => Number(item.commodityId)),
    );

    /** 当前选中渠道需透传的参数 */
    const commodityModelList = computed(() => selectedCommodity.value?.map(({
      commodityId, proApplyModel, abnormalProduct, followLoan, strategyAsset,
    }) => ({
      commodityId, proApplyModel, abnormalProduct, followLoan, strategyAsset,
    } as const)) || []);

    /** 计算当前页面配置类型 */
    const trackCurrent = computed(() => {
      if (isDcHalfApply.value) {
        return PreApplyPageTypeEnum.dcHalf;
      }
      if (applyChooseList.value && applyChooseList.value.autarky) {
        return PreApplyPageTypeEnum.autarky;
      }
      return PreApplyPageTypeEnum.normal;
    });

    const getPaddingBottomClass = () => {
      if (commodityIdList.value.length) {
        const {
          isSuperMemberCardShow, hasBuy,
        } = SuperMemberCard.getSuperMember();
        if (isSuperMemberCardShow) {
          showMemberCard.value = true;
          if (hasBuy) {
            return 'card--bought';
          }
          needBuySuperMember.value = true;
          return 'card--unbought';
        }
      }
      return '';
    };

    /** 请求协议页协议阅读限制，阅读全部协议||拉到底部 */
    const getContractRule = async (ids: number[]) => {
      try {
        contractRule.value = await getAbTestContract(
          ContractSceneTypeEnum.credit, ids,
        );
      } catch (error) {
        ubtSendError({
          name: 'Failed to fetch adv data',
          message: String(error),
        });
      }
    };

    /** 获取实验缓存key */
    const getExperimentalKey = () => `AppFinanceH5Web_${store.state.base.originUserInfo.mobile}_hitExperimental`;

    /** 获取协议页ab版本 */
    const getContractVersion = async () => {
      const experimentalKey = getExperimentalKey();
      // 1. 首先检查本地存储
      const hitExperimental = await nativeLocalGetData({ key: experimentalKey });
      if (String(hitExperimental) === ABResEnum.HINT) {
        pageVersion.value = ABResEnum.HINT;
        return;
      }

      // 2. 判断是否为新客
      if (applyChooseList.value?.newCustomer) {
        // 新客请求ab实验
        const { result } = await getGalileoAb().getAbResCatchError('202504281415419332', '202504281415419419') || {};
        pageVersion.value = result;

        // 3. 如果ab实验结果为1，则缓存数据
        if (String(result) === ABResEnum.HINT) {
          await nativeLocalSetData({ key: experimentalKey, value: [ABResEnum.HINT] });
        }
      } else {
        // 非新客直接设置为0
        pageVersion.value = ABResEnum.WAITHINT;
      }
    };

    /** 滚动处理 */
    const handleScroll = debounce(() => {
      const element = agreementContentRef.value;
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollHeight - scrollTop - clientHeight <= 10) {
        arriveBottom.value = true;
        // 页面滑动到底部 埋点
        ubtSetupPageButton('finance_agreementSlideEnd');
        element.removeEventListener('scroll', handleScroll);
      }
    }, 100);

    /** 需要强制阅读时判断是否需要监听滚动到页底
     * contractRule需先于channelList获取到，否则面高度是否超过一屏计算会出现问题
     */
    const listenerPageBottom = () => {
      const element = agreementContentRef.value;
      if (!element) return;

      const { clientHeight, scrollHeight } = element;
      // 协议未满一屏，无需强制下拉到底部
      if (clientHeight >= scrollHeight) return;
      if (contractRule.value.completeRead) {
        // 如果规则为强制阅读，才需要展示强制阅读
        arriveBottom.value = false;
      }
      element.addEventListener('scroll', handleScroll);
    };

    /** 当agreementContentRef被挂在后，监听滚动事件 */
    watch(() => agreementContentRef.value, (newVal) => {
      if (newVal) {
        listenerPageBottom();
      }
    });

    /** 初始化会员卡片 */
    const initSuperMemberCard = async () => {
      try {
        const element = document.getElementById('credit_button_wrapper');
        /**
           * 只在有正常授信分发的渠道时(半流程授信不算)，初始化superMemberCard
           */
        if (element) {
          // 根据场景传入不同的来源
          await SuperMemberCard.init({
            commoditys: commodityIdList.value,
            from: isDcHalfApply.value
              ? SuperMemberSourceEnum.FINANCE_DCHALFAPPLY_AGREEMENT
              : SuperMemberSourceEnum.CREDIT_AGREEMENT,
          }, element);
          paddingBottomClass.value = getPaddingBottomClass();
        }
      } catch {
        // 初始化失败
      }
    };

    /** 获取协议 */
    const getAgreements = async () => {
      const data = isDcHalfApply.value
        ? await getDcContractList(commodityIdList.value)
        : await getAgreementsByCommodities({
          commodityIds: commodityIdList.value,
          type: ContractEnum.PREAPPLY,
        });
      return data;
    };

    /** 获取埋点额外参数 */
    const getUbtExtraParams = () => ({
      pageVersion: pageVersion.value,
      applyMode: applyChooseList.value?.applyMode || '',
      ...(serviceUbtExtra.value || {}),
    });

    /** 检查用户停留时长是否达标 */
    const checkUserStayTimeReachTask = () => {
      const { preCreditDelayTime = 30 } = applyChooseList.value || {};
      const reachTaskTime = preCreditDelayTime * 1000;

      /** 设置用户实际查看协议的开始时间 */
      context.emit('setUserViewAgreementStartTime', Date.now());

      userViewAgreementTimer.value = setTimeout(() => {
        /** 检验停留时间是否达标，避免未清除定时器导致的错误上报 */
        if (Date.now() - props.userViewAgreementStartTime >= reachTaskTime) {
          context.emit('reportUserStayStatus', UserStayStatusSceneEnum.TIME_REACH);
        }
      }, reachTaskTime);
    };

    /** 获取所有页面内容协议 */
    const getPageData = async () => {
      try {
        /** 获取协议列表数据和影响页面展示/布局的数据，必须一起请求，否则页面动态布局会出现问题 */
        const results = await Promise.allSettled([
          getAgreements(),
          getContractRule(commodityIdList.value),
          !props.isOnlyDcHalf && initSuperMemberCard(),
          !isDcHalfApply.value && getContractVersion(),
        ]);
        let data: Commodity[] = [];

        if (results[0].status === 'fulfilled') {
          data = results[0].value;
        } else {
          throw new Error('getAgreements error');
        }
        // 进入页面 协议曝光埋点数据
        const protocolExposeListData: ProtocolExposeDataType[] = [];
        channelList.value = commodities.value.reduce((acc, item, idx) => {
          const agreement = data.find(
            (info) => Number(info.productId) === Number(item.commodityId),
          ) || { contractList: [], creditRouteChannel: undefined };

          protocolExposeListData.push({
            supplierCode: item.commodityId,
            agreementName: agreement?.contractList?.[0]?.name || '',
            commodityId: item.commodityId,
            commodityName: item.commodityName,
            position: idx + 1,
          });

          const newItem = {
            ...item,
            contractList: agreement.contractList,
            creditRouteChannel: agreement.creditRouteChannel,
            // 是否选中该渠道
            isSelected: true,
          };

          return newItem.contractList?.length ? [...acc, newItem] : acc;
        }, [] as CommodityPo.Info[]);

        const filteredChannelList = channelList.value.filter(
          /** 若剔除跟贷渠道后没有获取到任何渠道协议，则按不展示任何协议逻辑处理
             * 主要是兜底跟贷需求中协议获取异常兜底
             */
          (item) => item.followLoan !== true,
        );

        if (filteredChannelList.length) {
          context.emit('loadAgreements');

          /** 添加定时器，检查用户协议查看时长是否达标 */
          checkUserStayTimeReachTask();

          const { exposeAgreement } = trackConfig[trackCurrent.value];
          ubtSetupModuleExpose(exposeAgreement, {
            channelList: protocolExposeListData,
            ...getUbtExtraParams(),
          });
        } else {
          ubtSetupModuleExpose('finance_loan_credit_agreementEmpty', {
            commodityIds: commodityIdList.value,
            isDcHalfApply: isDcHalfApply.value,
            channelList: channelList.value,
            ...getUbtExtraParams(),
          });
          chooseNoChannelRouter();
        }
      } catch (error) {
        ubtSendError(error as Error);
      }
    };

    /** 滚动到页底 */
    const scrollToBottom = () => {
      if (contractBottomRef.value && !arriveBottom.value) {
        contractBottomRef.value.scrollIntoView({ behavior: 'smooth' });
        arriveBottom.value = true;
      }
    };

    /** 取消 / 选中渠道 */
    const selectChannel = (id: string) => {
      const channel = channelList.value.find((item) => item.commodityId === Number(id));
      if (channel) {
        channel.isSelected = !channel.isSelected;
        // 勾选/取消勾选埋点
        if (isDcHalfApply.value) {
          ubtSetupPageButton('finance_dcAgreementSelectChannel', {
            isSelect: channel.isSelected,
            commodityId: id,
            ...getUbtExtraParams(),
          });
        }
      }
    };

    /** 切换协议 */
    const changeContractIndex = (channel: CommodityPo.Info, contract: Contract) => {
      /** 子组件抛出事件，外层统一埋点 */
      const { agreementClick } = trackConfig[trackCurrent.value];
      ubtSetupPageButton(agreementClick, {
        agreementName: contract.name,
        supplierCode: channel.commodityId,
        isopen: channel.isExpand,
        ...getUbtExtraParams(),
      });
    };

    /**  点击授信协议同意按钮埋点 */
    const ubtPageButtonTrack = (type: ButtonStatusEnum) => {
      const { btnName } = trackConfig[trackCurrent.value];
      ubtSetupPageButton(btnName, {
        flagValue: String(commodityIds.value),
        extraValue: type === ButtonStatusEnum.countDown ? 'countdownNotClick' : 'canClick',
        differValue: String(applyChooseList.value && applyChooseList.value.isStock),
        ...getUbtExtraParams(),
      });
    };

    const onSubmit = async () => {
      /** 是否要开启定位检查 */
      const shouldEnableLocationCheck = await getWisdomConfig<boolean>('locationCheckSwitch');
      if (shouldEnableLocationCheck) {
        const locationIsOpen = await checkIsOpenGPS();
        if (!locationIsOpen) {
          return;
        }
      }
      ubtPageButtonTrack(ButtonStatusEnum.avaliable);
      // 查找是否返回的征信协议
      const hasCreditRouteChannel = channelList.value.filter((item) => item.creditRouteChannel);
      // 存储授信提交时需要携带的参数
      updateModuleState('preApplyChoose', {
        creditRouteChannel: hasCreditRouteChannel[0]
          ? hasCreditRouteChannel[0].creditRouteChannel : undefined,
        submitCommodityIds: commodityIds.value,
        commodityModelList: commodityModelList.value,
      });
      context.emit('submit');
    };

    const onCountDown = () => {
      ubtPageButtonTrack(ButtonStatusEnum.countDown);
    };

    onMounted(async () => {
      if (commodityIdList.value?.length > 0) {
        await getPageData();
        const { exposeName } = trackConfig[trackCurrent.value];
        ubtSetupModuleExpose(exposeName, {
          flagValue: String(commodityIds.value),
          extraValue: isParallel.value ? '002' : '001',
          differValue: String(applyChooseList.value && applyChooseList.value.isStock),
          ...getUbtExtraParams(),
        });
      }
    });

    onUnmounted(() => {
      /** 父组件被卸载时需重置会员卡实例 */
      SuperMemberCard.destroyInstance();
      /** 移除定时器 */
      if (userViewAgreementTimer.value) {
        clearTimeout(userViewAgreementTimer.value);
      }
      /** 移除监听 */
      const element = agreementContentRef.value;
      if (element) element.removeEventListener('scroll', handleScroll);
    });

    return {
      channelList,
      isParallel,
      pageVersion,
      showMemberCard,
      paddingBottomClass,
      arriveBottom,
      contractRule,
      superMemberButtonText,
      needSupplement,
      ButtonTypeEnum,
      isDcHalfApply,
      agreementContentRef,
      contractBottomRef,
      ABResEnum,
      selectChannel,
      changeContractIndex,
      scrollToBottom,
      onSubmit,
      onCountDown,
    };
  },
});
